import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, ShieldCheck, AlertCircle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "Réinitialiser mon mot de passe · PROXIreduc-28";
  }, []);

  const strength = useMemo(() => {
    let s = 0;
    if (pwd.length >= 8) s++;
    if (/[A-Z]/.test(pwd)) s++;
    if (/[a-z]/.test(pwd)) s++;
    if (/[0-9]/.test(pwd)) s++;
    if (/[^A-Za-z0-9]/.test(pwd)) s++;
    return s; // 0..5
  }, [pwd]);
  const strengthLabel = ["Très faible","Faible","Moyen","Correct","Fort","Excellent"][strength];
  const strengthColor = ["bg-slate-200","bg-red-400","bg-orange-400","bg-amber-400","bg-emerald-500","bg-emerald-600"][strength];

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd.length < 8) { setErr("Le mot de passe doit faire au moins 8 caractères."); return; }
    if (pwd !== pwd2) { setErr("Les deux mots de passe ne sont pas identiques."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success("Mot de passe réinitialisé !");
      setTimeout(() => navigate("/connexion"), 1800);
    } catch (e) {
      setErr(formatApiError(e) || "Lien invalide ou expiré.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-4 py-16" data-testid="reset-password-page">
        <div className="bg-white border border-red-200 rounded-2xl p-6" data-testid="reset-missing-token">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 grid place-items-center shrink-0">
              <AlertCircle size={22}/>
            </div>
            <div className="flex-1">
              <div className="font-heading font-extrabold text-slate-900">Lien incomplet</div>
              <p className="mt-1 text-sm text-slate-600">
                Ce lien de réinitialisation ne contient pas de jeton valide. Faites une nouvelle demande.
              </p>
              <div className="mt-4">
                <Link to="/mot-de-passe-oublie">
                  <Button className="btn-shine text-white h-10" data-testid="reset-new-request">Demander un nouveau lien</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-md mx-auto px-4 py-16" data-testid="reset-password-page">
        <div className="bg-white border border-emerald-200 rounded-2xl p-6" data-testid="reset-success-panel">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
              <ShieldCheck size={22}/>
            </div>
            <div className="flex-1">
              <div className="font-heading font-extrabold text-slate-900">Mot de passe mis à jour</div>
              <p className="mt-1 text-sm text-slate-600">Vous allez être redirigé vers la page de connexion…</p>
              <div className="mt-4">
                <Link to="/connexion">
                  <Button className="btn-shine text-white h-10" data-testid="reset-goto-login">Se connecter maintenant</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="reset-password-page">
      <h1 className="font-heading text-3xl font-extrabold">Nouveau mot de passe</h1>
      <p className="mt-2 text-sm text-slate-600">
        Choisissez un mot de passe solide d'au moins 8 caractères. Ce lien expire 30 minutes après l'envoi.
      </p>
      <form onSubmit={submit} className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 space-y-4"
            data-testid="reset-password-form">
        <div>
          <Label>Nouveau mot de passe</Label>
          <div className="mt-1 relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input type={show ? "text" : "password"} required value={pwd}
                   onChange={(e) => setPwd(e.target.value)}
                   className="pl-9 pr-10" minLength={8} maxLength={128}
                   data-testid="reset-pwd-input"/>
            <button type="button" onClick={() => setShow((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                    aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    data-testid="reset-pwd-toggle">
              {show ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
          </div>
          {/* Jauge de robustesse */}
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${strengthColor} transition-all`}
                   style={{ width: `${(strength / 5) * 100}%` }}/>
            </div>
            <span className="text-[11px] font-semibold text-slate-500 w-20 text-right"
                  data-testid="reset-pwd-strength">
              {pwd ? strengthLabel : ""}
            </span>
          </div>
        </div>
        <div>
          <Label>Confirmer le mot de passe</Label>
          <div className="mt-1 relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input type={show ? "text" : "password"} required value={pwd2}
                   onChange={(e) => setPwd2(e.target.value)}
                   className="pl-9" minLength={8} maxLength={128}
                   data-testid="reset-pwd2-input"/>
          </div>
        </div>

        {err && (
          <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm p-3"
               data-testid="reset-error">
            {err}
          </div>
        )}

        <Button type="submit" disabled={loading || !pwd || !pwd2}
                className="w-full btn-shine text-white h-11"
                data-testid="reset-submit-btn">
          {loading ? "Mise à jour…" : "Mettre à jour mon mot de passe"}
        </Button>
        <div className="text-center text-sm text-slate-500">
          <Link to="/connexion" className="text-[#E05638] font-semibold">Retour à la connexion</Link>
        </div>
      </form>
    </div>
  );
}
